/**
 * Create a deterministic hash for arbitrarily nested JSON-like values.
 * Objects are canonicalised by sorting keys so that different key orders
 * produce the same hash. Undefined values are stripped to mirror JSON semantics.
 */
export function stableHash(value: unknown): string {
  const seen = new WeakMap<object, number>();
  let nextId = 0;

  function serialize(input: unknown): string {
    if (input === null || typeof input === "number" || typeof input === "boolean") {
      return JSON.stringify(input);
    }

    if (typeof input === "string") {
      return JSON.stringify(input);
    }

    if (Array.isArray(input)) {
      return `[${input.map((item) => serialize(item)).join(",")}]`;
    }

    if (typeof input === "object" && input) {
      const existingId = seen.get(input);
      if (existingId !== undefined) {
        return `{"$ref":${existingId}}`;
      }
      seen.set(input as object, nextId++);

      const entries = Object.entries(input as Record<string, unknown>)
        .filter(([, v]) => v !== undefined)
        .map(([key, val]) => [key, serialize(val)] as const)
        .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));

      return `{${entries.map(([key, val]) => `${JSON.stringify(key)}:${val}`).join(",")}}`;
    }

    return JSON.stringify(input ?? null);
  }

  return hashString(serialize(value));
}

function hashString(input: string): string {
  let hash = 5381;
  for (let i = 0; i < input.length; i += 1) {
    hash = (hash * 33) ^ input.charCodeAt(i);
  }
  return (hash >>> 0).toString(16);
}

export function isStableHashEqual(a: unknown, b: unknown): boolean {
  return stableHash(a) === stableHash(b);
}



